import { Beacon, ensureReference, } from "@knyt/artisan";
import { EventListenerController } from "./EventListenerController.js";
/**
 * A factory and manger for event listener controllers.
 *
 * @alpha
 */
export class EventListenerManager {
    #host;
    #target$;
    #controllers = [];
    constructor(host, target) {
        const target$ = ensureReference(target);
        this.#host = host;
        this.#target$ = target$;
    }
    /**
     * Creates a new event listener controller for the given event name
     * and listener, and adds it to the manager.\
     *
     * @returns An instance of `EventListenerController` that can be used to manage the event listener.
     */
    create(eventName, listener, options) {
        const controller = new EventListenerController(this.#host, {
            eventName,
            listener,
            target$: this.#target$,
            options,
        });
        this.#controllers.push(controller);
        return controller;
    }
    /**
     * Adds a new event listener controller to the manager
     * for the given event name and listener.
     *
     * @returns The current instance of `EventListenerManager` for chaining.
     */
    add(eventName, listener, options) {
        this.create(eventName, listener, options);
        return this;
    }
    /**
     * Creates an observable that emits events of the given type
     * from the target element when the event is fired.
     *
     * @returns An observable that emits events of the specified type.
     */
    observe(eventName, options) {
        const signaler = Beacon.withEmitter();
        const controller = this.create(eventName, signaler.next, options);
        controller.disposeSignal.subscribe(signaler.complete);
        return signaler.beacon;
    }
    /**
     * Adds all event listeners to the target element.
     */
    setup() {
        for (const controller of this.#controllers) {
            controller.setup();
        }
    }
    /**
     * Removes all event listeners from the target element.
     */
    teardown() {
        for (const controller of this.#controllers) {
            controller.teardown();
        }
    }
    /**
     * Disposes of all event listeners from the target element
     */
    dispose() {
        for (const controller of this.#controllers) {
            controller.dispose();
        }
        this.#controllers = [];
    }
}
